import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import { billingAPI, formsAPI, meAPI, platformAPI } from '../api/crm'
import { accessFor, applyFieldAccess, usePermissions } from './usePermissions'

/** Query keys include the tenant so switching tenants never shows another tenant's cache. */
export const useTenantKey = () => useSelector((state) => state.tenant.current?.tenantId)

/** The workspace's subscription (phase, plan, limits); usage only for admins. */
export const useSubscription = () => {
  const tenantId = useTenantKey()
  return useQuery({
    queryKey: [tenantId, 'billing', 'subscription'],
    queryFn: billingAPI.subscription,
    enabled: !!tenantId,
    staleTime: 60_000,
    retry: false,
  })
}

export const useMyTenants = () =>
  useQuery({ queryKey: ['me', 'tenants'], queryFn: meAPI.tenants })

/** Platform admin = the platform endpoint answers; it returns 403 for everyone else. */
export const useIsPlatformAdmin = () => {
  const query = useQuery({
    queryKey: ['platform', 'probe'],
    queryFn: platformAPI.listTenants,
    retry: false,
    staleTime: 5 * 60_000,
  })
  return query.isSuccess
}

/** Published forms of the tenant, grouped by entity type → drives navigation. */
export const usePublishedForms = () => {
  const tenantId = useTenantKey()
  return useQuery({
    queryKey: [tenantId, 'forms', { status: 'PUBLISHED' }],
    queryFn: () => formsAPI.list({ status: 'PUBLISHED' }),
    enabled: !!tenantId,
  })
}

/** Record types the member may view (role permissions), and all published forms. */
export const useEntityTypes = () => {
  const { data = [], ...rest } = usePublishedForms()
  const { data: perms } = usePermissions()
  const entityTypes = [...new Set(data.map((f) => f.entityType))].sort().filter((t) => accessFor(perms, t).canView)
  return { entityTypes, forms: data, ...rest }
}

/** The published form as the member's role sees it (hidden fields removed, read-only ones locked). */
export const usePublishedForm = (code) => {
  const tenantId = useTenantKey()
  const { data: perms } = usePermissions()
  const query = useQuery({
    queryKey: [tenantId, 'forms', 'published', code],
    queryFn: () => formsAPI.published(code),
    enabled: !!tenantId && !!code,
  })
  const data = useMemo(() => applyFieldAccess(query.data, perms), [query.data, perms])
  return { ...query, data }
}

export const useFieldTypes = () =>
  useQuery({ queryKey: ['fieldTypes'], queryFn: formsAPI.fieldTypes, staleTime: Infinity })

/** What this installation offers ({ emailEnabled, maxFileSizeMb }); rarely changes. */
export const useFeatures = () => {
  const { data } = useQuery({ queryKey: ['me', 'features'], queryFn: meAPI.features, staleTime: 10 * 60_000 })
  return data || { emailEnabled: false, maxFileSizeMb: 20 }
}

/** The signed-in person's profile ({ email, displayName }); shared by every workspace. */
export const useProfile = () => useQuery({ queryKey: ['me', 'profile'], queryFn: meAPI.profile, staleTime: 5 * 60_000 })
